import * as React from "react";
import { useId } from "react-id-generator";

/**
 * A single labeled panel inside a <Tabs> group.
 *
 * `Tab` itself renders nothing on its own - `Tabs` reads each child's
 * `label`/`children` props and takes care of all the actual markup. This
 * keeps the MDX usage declarative:
 *
 *   <Tabs>
 *   <Tab label="evConfig.yaml">
 *
 *   ```yaml
 *   scan:
 *     iframes: true
 *   ```
 *
 *   </Tab>
 *   </Tabs>
 */
export const Tab = ({ children }) => children;

/**
 * Accessible, SSR-safe tabbed code switcher used to show the same setting
 * expressed in different formats (evConfig.yaml / evConfig.json / modern
 * code / legacy code).
 *
 * - Every panel is always present in the rendered markup; inactive panels
 *   are hidden with the plain HTML `hidden` attribute, so the first tab's
 *   content is visible to no-JS readers and crawlers, and nothing needs to
 *   wait for hydration to be readable.
 * - Keyboard support follows the standard tabs pattern: Left/Right (and
 *   Home/End) move focus and selection between tabs.
 */
const Tabs = ({ children }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [instanceId] = useId(1, "ev-tabs");
  const tabRefs = React.useRef([]);

  const tabs = React.Children.toArray(children).filter(
    child => React.isValidElement(child)
  );

  if (tabs.length === 0) {
    return null;
  }

  const focusTab = index => {
    const el = tabRefs.current[index];
    if (el) {
      el.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActiveIndex(nextIndex);
    focusTab(nextIndex);
  };

  return (
    <div className="tabsWrapper">
      <div className="tabsList" role="tablist" aria-label="Configuration examples">
        {tabs.map((tab, index) => {
          const tabId = `${instanceId}-tab-${index}`;
          const panelId = `${instanceId}-panel-${index}`;
          const isActive = index === activeIndex;
          return (
            <button
              key={tabId}
              type="button"
              id={tabId}
              role="tab"
              ref={el => {
                tabRefs.current[index] = el;
              }}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              className={isActive ? "tabsTab tabsTabActive" : "tabsTab"}
              onClick={() => setActiveIndex(index)}
              onKeyDown={event => handleKeyDown(event, index)}
            >
              {tab.props.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab, index) => {
        const tabId = `${instanceId}-tab-${index}`;
        const panelId = `${instanceId}-panel-${index}`;
        const isActive = index === activeIndex;
        return (
          <div
            key={panelId}
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId}
            hidden={!isActive}
            className="tabsPanel"
          >
            {tab.props.children}
          </div>
        );
      })}
    </div>
  );
};

export default Tabs;
